/* ==========================================================================
   Sunboost custom — sb-hero-video web component
   Clean rebuild: 2026-04-14 (v2)

   Responsibilities:
   - Native autoplay (muted, playsinline) — browser starts the video.
   - One transparent center button cycles state:
       unmute -> pause -> play -> pause -> play -> ...
     (initial click unmutes AND ensures playing; subsequent clicks toggle.)
   - Mobile source swap (one-shot at connect; never on resize).
   - Auto-hide text overlay on first 'playing' event (opt-in).
   - Pause-on-hamburger-menu-open (opt-in).
   - Tab-visibility recovery (resume when tab returns) — only if user did
     NOT explicitly pause via the cycle button.

   Custom element: <sb-hero-video>
   ========================================================================== */

(function () {
  if (window.customElements && window.customElements.get('sb-hero-video')) return;

  var NOOP = function () {};

  // True while the user has explicitly paused via the cycle button
  // (cycle state is 'play', which means "click to play").
  function userHasPaused(el) {
    return el.cycleBtn && el.cycleBtn.dataset.state === 'play';
  }

  class SbHeroVideo extends HTMLElement {
    constructor() {
      super();
      this._menuObserver = null;
      this._menuDrawer = null;
      this._idleTimer = null;
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onCycleClick = this._onCycleClick.bind(this);
      this._onFullscreenClick = this._onFullscreenClick.bind(this);
      this._onMenuMutation = this._onMenuMutation.bind(this);
      this._onHostMouseMove = this._onHostMouseMove.bind(this);
      this._onHostMouseLeave = this._onHostMouseLeave.bind(this);
    }

    connectedCallback() {
      this.video    = this.querySelector('.sb-hero-video__video');
      this.content  = this.querySelector('.sb-hero-video__content');
      this.cycleBtn = this.querySelector('.js-cycle');
      this.fsBtn    = this.querySelector('.js-fullscreen');
      this.cover    = this.querySelector('.sb-hero-video__loading-cover');

      if (!this.video) return;

      this._maybeSwapMobileSources();
      this._maybeAutohideText();

      document.addEventListener('visibilitychange', this._onVisibilityChange);

      if (this.cycleBtn) {
        this.cycleBtn.addEventListener('click', this._onCycleClick);
      }

      if (this.fsBtn) {
        this.fsBtn.addEventListener('click', this._onFullscreenClick);
      }

      // Idle-hide: on hover-capable devices, hide controls after 2.5s of
      // mouse stillness over the hero. Any mousemove resets the timer and
      // re-shows them. CSS handles the actual show/hide via the .is-idle
      // class on the host.
      this.addEventListener('mousemove', this._onHostMouseMove);
      this.addEventListener('mouseleave', this._onHostMouseLeave);

      // Sync .is-playing on host for hover-only button visibility (CSS).
      var self = this;
      var syncPlayingClass = function () {
        var playing = !self.video.paused && !self.video.ended;
        self.classList.toggle('is-playing', playing);
      };
      this.video.addEventListener('play', syncPlayingClass);
      this.video.addEventListener('playing', syncPlayingClass);
      this.video.addEventListener('pause', syncPlayingClass);
      this.video.addEventListener('ended', syncPlayingClass);
      syncPlayingClass();

      // Hide the loading cover image on the first real frame ('playing'
      // fires after the first decoded frame is on screen). This is more
      // reliable than the native <video> poster attribute which some
      // browsers tear down before the first frame paints.
      this.video.addEventListener(
        'playing',
        function () { self.classList.add('is-loaded'); },
        { once: true }
      );

      // Belt-and-braces autoplay kick. The native `autoplay` attribute is
      // the primary trigger, but some browsers (especially after a deferred
      // script load or with strict autoplay policies) need an explicit
      // .play() to start. Muted is required for autoplay to be allowed.
      try {
        this.video.muted = true;
        var p = this.video.play();
        if (p && typeof p.catch === 'function') p.catch(NOOP);
      } catch (e) { /* noop */ }

      if (this.dataset.pauseOnMenuOpen === 'true') {
        this._wireMenuPauseObserver();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      if (this.cycleBtn) {
        this.cycleBtn.removeEventListener('click', this._onCycleClick);
      }
      if (this.fsBtn) {
        this.fsBtn.removeEventListener('click', this._onFullscreenClick);
      }
      this.removeEventListener('mousemove', this._onHostMouseMove);
      this.removeEventListener('mouseleave', this._onHostMouseLeave);
      if (this._idleTimer) {
        clearTimeout(this._idleTimer);
        this._idleTimer = null;
      }
      if (this._menuObserver) {
        this._menuObserver.disconnect();
        this._menuObserver = null;
      }
    }

    /* ── Idle-hide handlers — class toggling, CSS does the visual work. */
    _onHostMouseMove() {
      this.classList.remove('is-idle');
      if (this._idleTimer) clearTimeout(this._idleTimer);
      var self = this;
      this._idleTimer = window.setTimeout(function () {
        self.classList.add('is-idle');
      }, 2500);
    }

    _onHostMouseLeave() {
      this.classList.remove('is-idle');
      if (this._idleTimer) {
        clearTimeout(this._idleTimer);
        this._idleTimer = null;
      }
    }

    /* ── Mobile source swap (one-shot at connect) ───────────────────── */
    _maybeSwapMobileSources() {
      if (this.dataset.hasMobileVideo !== 'true') return;

      var bp = parseInt(this.dataset.mobileBreakpoint || '749', 10);
      if (!window.matchMedia('(max-width: ' + bp + 'px)').matches) return;

      var jsonEl = this.querySelector('script.sb-hero-video__mobile-srcs');
      if (!jsonEl) return;

      var sources = [];
      try {
        sources = JSON.parse(jsonEl.textContent || '[]');
      } catch (err) {
        return;
      }
      if (!sources.length) return;

      var video = this.video;
      var existing = video.querySelectorAll('source');
      for (var i = 0; i < existing.length; i++) existing[i].remove();

      sources.forEach(function (src) {
        if (!src || !src.url) return;
        var el = document.createElement('source');
        el.src = src.url;
        if (src.type) el.type = src.type;
        video.appendChild(el);
      });

      var mobilePoster = this.dataset.mobilePoster;
      if (mobilePoster) video.poster = mobilePoster;

      try { video.load(); } catch (err) { /* ignore */ }
    }

    /* ── Auto-hide text overlay once video starts playing ───────────── */
    _maybeAutohideText() {
      if (this.dataset.autohideText !== 'true') return;
      if (!this.content || !this.video) return;

      var self = this;
      this.video.addEventListener(
        'playing',
        function () {
          if (self.content) {
            self.content.classList.add('is-hidden');
            self.content = null;
          }
        },
        { once: true }
      );
    }

    /* ── Visibility recovery ────────────────────────────────────────── */
    _onVisibilityChange() {
      if (document.hidden) return;
      if (!this.video || !this.video.paused || this.video.ended) return;
      if (userHasPaused(this)) return;
      var p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(NOOP);
    }

    /* ── Cycle button: unmute → pause → play → pause → ... ──────────── */
    _onCycleClick(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (!this.video || !this.cycleBtn) return;

      var state = this.cycleBtn.dataset.state || 'unmute';

      if (state === 'unmute') {
        // Bug fix: if the user clicks unmute before the video has actually
        // loaded enough data to play with sound, the previous catch-handler
        // re-muted the video silently — leaving the cycle button claiming
        // "playing unmuted" while audio was actually off. Now we:
        //   1. mark user intent (`_wantsSound`) and apply unmute + volume
        //   2. re-apply unmute on the next loadeddata / canplay / playing
        //      events in case the browser dropped our setting before the
        //      audio track was ready
        //   3. on play() rejection, just no-op — never re-mute
        var self = this;
        self._wantsSound = true;

        var applyUnmute = function () {
          if (!self._wantsSound || !self.video) return;
          if (self.video.muted) self.video.muted = false;
          if (self.video.volume === 0) self.video.volume = 1;
        };
        applyUnmute();
        self.video.addEventListener('loadeddata', applyUnmute, { once: true });
        self.video.addEventListener('canplay',    applyUnmute, { once: true });
        self.video.addEventListener('playing',    applyUnmute, { once: true });

        var p = self.video.play();
        if (p && typeof p.catch === 'function') p.catch(NOOP);
        this._setCycleState('pause');
      } else if (state === 'pause') {
        this.video.pause();
        this._setCycleState('play');
      } else if (state === 'play') {
        var p2 = this.video.play();
        if (p2 && typeof p2.catch === 'function') p2.catch(NOOP);
        this._setCycleState('pause');
      }
    }

    /* ── Fullscreen — TOGGLE: enter when not in fullscreen, exit when in.
     *    Requests on the host element so HTML chrome stays available;
     *    iOS Safari uses webkitEnterFullscreen on the <video> (the native
     *    iOS player has its own exit affordance). ───────────────────── */
    _onFullscreenClick(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (!this.video) return;

      // Currently fullscreen → exit.
      var fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      if (fsEl) {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(NOOP);
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
        return;
      }

      // Not in fullscreen → enter.
      if (typeof this.video.webkitEnterFullscreen === 'function' && /iPhone|iPad|iPod/.test(navigator.userAgent)) {
        this.video.webkitEnterFullscreen();
        return;
      }
      var target = this;
      if (target.requestFullscreen) {
        target.requestFullscreen().catch(NOOP);
      } else if (target.webkitRequestFullscreen) {
        target.webkitRequestFullscreen();
      } else if (this.video.requestFullscreen) {
        this.video.requestFullscreen().catch(NOOP);
      }
    }

    _setCycleState(state) {
      if (!this.cycleBtn) return;
      this.cycleBtn.dataset.state = state;

      var iconUnmute = this.cycleBtn.querySelector('.sb-hv-icon--unmute');
      var iconPause  = this.cycleBtn.querySelector('.sb-hv-icon--pause');
      var iconPlay   = this.cycleBtn.querySelector('.sb-hv-icon--play');

      if (iconUnmute) iconUnmute.classList.toggle('sb-hv-icon--hidden', state !== 'unmute');
      if (iconPause)  iconPause.classList.toggle('sb-hv-icon--hidden',  state !== 'pause');
      if (iconPlay)   iconPlay.classList.toggle('sb-hv-icon--hidden',   state !== 'play');

      var labels = { unmute: 'Unmute video', pause: 'Pause video', play: 'Play video' };
      this.cycleBtn.setAttribute('aria-label', labels[state] || 'Video controls');
    }

    /* ── Hamburger menu pause hook ──────────────────────────────────── */
    _wireMenuPauseObserver() {
      var drawer =
        document.querySelector('header-drawer details') ||
        document.querySelector('[id*="Details-menu-drawer"]') ||
        document.querySelector('details[class*="drawer"]');

      if (!drawer) {
        if (window.Shopify && window.Shopify.designMode) {
          // eslint-disable-next-line no-console
          console.warn('[sb-hero-video] menu drawer not found; pause-on-menu-open disabled');
        }
        return;
      }

      this._menuDrawer = drawer;
      this._menuObserver = new MutationObserver(this._onMenuMutation);
      this._menuObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }

    _onMenuMutation() {
      if (!this._menuDrawer || !this.video) return;
      if (this._menuDrawer.hasAttribute('open')) {
        this.video.pause();
      } else {
        if (userHasPaused(this)) return;
        var p = this.video.play();
        if (p && typeof p.catch === 'function') p.catch(NOOP);
      }
    }
  }

  window.customElements.define('sb-hero-video', SbHeroVideo);
})();
